import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useSectionAccess } from '../hooks/useSectionAccess';
import { getAllUsers, adminResetPassword, adminToggleUserStatus, getEmployees, register, downloadUsersBulkTemplate, bulkUploadUsers } from '../api';
import api from '../api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Users, Search, Key, Shield, ShieldOff, Eye, EyeOff, 
  RefreshCw, UserCheck, UserX, Mail, Clock, Edit2, Link, Unlink
} from 'lucide-react';
import { toast } from 'sonner';

const UserManagement = ({ user, setUser }) => {
  const [users, setUsers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Password reset dialog
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [resetting, setResetting] = useState(false);
  
  // Edit user dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ full_name: '', role: '', employee_id: '' });
  const [saving, setSaving] = useState(false);
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState('');

  // Create user dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({ full_name: '', email: '', password: '', role: 'employee' });

  // Bulk upload dialog
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [usersRes, empsRes] = await Promise.all([
        getAllUsers(),
        getEmployees()
      ]);
      setUsers(usersRes.data);
      setEmployees(empsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to fetch data: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await getAllUsers();
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      toast.error('Failed to fetch users: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const openResetDialog = (userToReset) => {
    setSelectedUser(userToReset);
    setNewPassword('');
    setConfirmPassword('');
    setShowPassword(false);
    setResetDialogOpen(true);
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 4) {
      toast.error('Password must be at least 4 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    try {
      setResetting(true);
      await adminResetPassword(selectedUser.id, newPassword);
      toast.success(`Password reset successfully for ${selectedUser.email}`);
      setResetDialogOpen(false);
      setSelectedUser(null);
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResetting(false);
    }
  };

  const openEditDialog = (userToEdit) => {
    setEditingUser(userToEdit);
    setEditForm({
      full_name: userToEdit.full_name || '',
      role: userToEdit.role || 'employee',
      employee_id: userToEdit.employee_id || ''
    });
    setEmployeeSearchTerm('');
    setEditDialogOpen(true);
  };

  const handleUpdateUser = async () => {
    if (!editingUser) return;
    
    try {
      setSaving(true);
      const updateData = {};
      
      if (editForm.full_name !== editingUser.full_name) {
        updateData.full_name = editForm.full_name;
      }
      if (editForm.role !== editingUser.role) {
        updateData.role = editForm.role;
      }
      // Always send employee_id to allow unlinking
      updateData.employee_id = editForm.employee_id || null;
      
      await api.put(`/admin/users/${editingUser.id}`, updateData);
      toast.success('User updated successfully');
      setEditDialogOpen(false);
      setEditingUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (userToToggle) => {
    const action = userToToggle.is_active !== false ? 'deactivate' : 'activate';
    if (!window.confirm(`Are you sure you want to ${action} ${userToToggle.email}?`)) {
      return;
    }

    try {
      const response = await adminToggleUserStatus(userToToggle.id);
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user status');
    }
  };

  // Get employees that are not already linked to any user
  const availableEmployees = employees.filter(emp => {
    const linkedUserIds = users
      .filter(u => u.employee_id)
      .map(u => u.employee_id);
    return !linkedUserIds.includes(emp.employee_id) || emp.employee_id === editForm.employee_id;
  }).filter(emp => 
    !employeeSearchTerm ||
    emp.name?.toLowerCase().includes(employeeSearchTerm.toLowerCase()) ||
    emp.employee_id?.toLowerCase().includes(employeeSearchTerm.toLowerCase())
  );

  const getLinkedEmployee = (userId) => {
    const userRecord = users.find(u => u.id === userId);
    if (!userRecord?.employee_id) return null;
    return employees.find(e => e.employee_id === userRecord.employee_id);
  };

  const getRoleBadge = (role) => {
    const roleColors = {
      admin: 'bg-red-100 text-red-700 border-red-200',
      hr_manager: 'bg-blue-100 text-blue-700 border-blue-200',
      accounts_manager: 'bg-purple-100 text-purple-700 border-purple-200',
      employee: 'bg-slate-100 text-slate-700 border-slate-200'
    };
    const roleLabels = {
      admin: 'Admin',
      hr_manager: 'HR Manager',
      accounts_manager: 'Accounts Manager',
      employee: 'Employee'
    };
    return (
      <Badge variant="outline" className={roleColors[role] || roleColors.employee}>
        {roleLabels[role] || role}
      </Badge>
    );
  };

  const filteredUsers = users.filter(u => 
    !searchQuery || 
    u.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.role?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const hasAccess = useSectionAccess('/user-management');
  if (hasAccess === false) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="flex items-center justify-center h-96">
          <Card className="w-96">
            <CardContent className="pt-6 text-center">
              <ShieldOff className="mx-auto h-12 w-12 text-red-500 mb-4" />
              <h2 className="text-xl font-semibold text-slate-900">Access Denied</h2>
              <p className="text-slate-500 mt-2">You don't have permission to access user management.</p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-6" data-testid="user-management-page">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">User Management</h1>
            <p className="text-slate-600 mt-1">Manage user accounts and reset passwords</p>
          </div>
          <Button variant="outline" onClick={fetchData} className="gap-2">
            <RefreshCw size={16} />
            Refresh
          </Button>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Users className="text-indigo-600" />
                  All Users ({users.length})
                </CardTitle>
                <CardDescription>
                  View and manage all user accounts in the system. Link users to employee records for proper org chart access.
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => { setBulkDialogOpen(true); setBulkResult(null); setBulkFile(null); }} data-testid="bulk-upload-btn">
                  <Users size={16} className="mr-2" />Bulk Upload
                </Button>
                <Button onClick={() => setCreateDialogOpen(true)} data-testid="create-user-btn">
                  <Users size={16} className="mr-2" />Create User
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Search */}
            <div className="mb-4">
              <div className="relative w-full max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                <Input
                  placeholder="Search by email, name, or role..."
                  className="pl-9"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid="user-search-input"
                />
              </div>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Employee Link</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.map((u) => {
                    const linkedEmp = getLinkedEmployee(u.id);
                    return (
                    <TableRow key={u.id} className={u.is_active === false ? 'bg-slate-50 opacity-60' : ''}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-semibold">
                            {u.full_name?.charAt(0)?.toUpperCase() || u.email?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{u.full_name || 'No name'}</p>
                            <p className="text-sm text-slate-500 flex items-center gap-1">
                              <Mail size={12} />
                              {u.email}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{getRoleBadge(u.role)}</TableCell>
                      <TableCell>
                        {linkedEmp ? (
                          <div className="flex items-center gap-2">
                            <Link size={14} className="text-green-600" />
                            <div>
                              <p className="text-sm font-medium text-slate-700">{linkedEmp.name}</p>
                              <p className="text-xs text-slate-500">{linkedEmp.employee_id}</p>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 text-amber-600">
                            <Unlink size={14} />
                            <span className="text-sm">Not linked</span>
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {u.is_active !== false ? (
                          <Badge className="bg-green-100 text-green-700 border-green-200">
                            <UserCheck size={12} className="mr-1" />
                            Active
                          </Badge>
                        ) : (
                          <Badge variant="destructive" className="bg-red-100 text-red-700 border-red-200">
                            <UserX size={12} className="mr-1" />
                            Inactive
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-slate-500 flex items-center gap-1">
                          <Clock size={12} />
                          {u.created_at ? new Date(u.created_at).toLocaleDateString() : 'N/A'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex justify-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEditDialog(u)}
                            className="gap-1"
                            data-testid={`edit-user-btn-${u.id}`}
                          >
                            <Edit2 size={14} />
                            Edit
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openResetDialog(u)}
                            className="gap-1"
                            data-testid={`reset-password-btn-${u.id}`}
                          >
                            <Key size={14} />
                            Password
                          </Button>
                          {u.id !== user.id && (
                            <Button
                              variant={u.is_active !== false ? "outline" : "default"}
                              size="sm"
                              onClick={() => handleToggleStatus(u)}
                              className={u.is_active !== false 
                                ? "gap-1 text-red-600 hover:text-red-700 hover:bg-red-50" 
                                : "gap-1 bg-green-600 hover:bg-green-700"}
                              data-testid={`toggle-status-btn-${u.id}`}
                            >
                              {u.is_active !== false ? (
                                <ShieldOff size={14} />
                              ) : (
                                <Shield size={14} />
                              )}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )})}
                  {filteredUsers.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                        No users found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Edit User Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Edit User</DialogTitle>
              <DialogDescription>
                Update user details and link to employee record
              </DialogDescription>
            </DialogHeader>
            
            {editingUser && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Full Name</Label>
                  <Input
                    value={editForm.full_name}
                    onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Role</Label>
                  <Select 
                    value={editForm.role} 
                    onValueChange={(v) => setEditForm({ ...editForm, role: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="hr_manager">HR Manager</SelectItem>
                      <SelectItem value="hr_executive">HR Executive</SelectItem>
                      <SelectItem value="accounts_manager">Accounts Manager</SelectItem>
                      <SelectItem value="ops_manager">Ops Manager</SelectItem>
                      <SelectItem value="manager">Manager</SelectItem>
                      <SelectItem value="employee">Employee</SelectItem>
                      <SelectItem value="warehouse_manager">Warehouse Manager</SelectItem>
                      <SelectItem value="state_head">State Head</SelectItem>
                      <SelectItem value="asm">ASM (Area Sales Manager)</SelectItem>
                      <SelectItem value="tl">Team Lead</SelectItem>
                      <SelectItem value="fse">Field Sales Executive (FSE)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Link to Employee Record</Label>
                  <p className="text-xs text-slate-500">
                    Linking allows the user to see their own data and hierarchy in the org chart
                  </p>
                  <Input
                    placeholder="Search employee..."
                    value={employeeSearchTerm}
                    onChange={(e) => setEmployeeSearchTerm(e.target.value)}
                  />
                  <div className="max-h-[200px] overflow-y-auto border rounded-lg">
                    <div
                      className={`p-3 cursor-pointer hover:bg-slate-50 border-b ${!editForm.employee_id ? 'bg-amber-50 border-amber-200' : ''}`}
                      onClick={() => setEditForm({ ...editForm, employee_id: '' })}
                    >
                      <p className="text-sm text-slate-600 flex items-center gap-2">
                        <Unlink size={14} />
                        No Employee Link
                      </p>
                    </div>
                    {availableEmployees.slice(0, 15).map(emp => (
                      <div
                        key={emp.id}
                        className={`p-3 cursor-pointer hover:bg-slate-50 border-b ${editForm.employee_id === emp.employee_id ? 'bg-green-50 border-green-200' : ''}`}
                        onClick={() => setEditForm({ ...editForm, employee_id: emp.employee_id })}
                      >
                        <p className="font-medium text-sm">{emp.name}</p>
                        <p className="text-xs text-slate-500">{emp.employee_id} • {emp.designation || 'No designation'}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdateUser} disabled={saving}>
                {saving ? <RefreshCw className="animate-spin mr-2" size={16} /> : null}
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reset Password Dialog */}
        <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Key className="text-indigo-600" />
                Reset Password
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-600">Resetting password for:</p>
                <p className="font-medium text-slate-900">{selectedUser?.email}</p>
                <p className="text-sm text-slate-500">{selectedUser?.full_name || 'No name'}</p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="new_password">New Password *</Label>
                <div className="relative">
                  <Input
                    id="new_password"
                    type={showPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    data-testid="new-password-input"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm_password">Confirm Password *</Label>
                <Input
                  id="confirm_password"
                  type={showPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  data-testid="confirm-password-input"
                />
              </div>

              {newPassword && confirmPassword && newPassword !== confirmPassword && (
                <p className="text-sm text-red-500">Passwords do not match</p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setResetDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleResetPassword} 
                disabled={resetting || !newPassword || newPassword !== confirmPassword}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="confirm-reset-btn"
              >
                {resetting ? 'Resetting...' : 'Reset Password'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create User Dialog */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Create New User</DialogTitle>
              <DialogDescription>
                Add a new user account. Pick the correct role — e.g., FSE, ASM, TL, State Head for inventory chain.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <Label>Full Name *</Label>
                <Input value={createForm.full_name} onChange={e => setCreateForm({ ...createForm, full_name: e.target.value })} data-testid="create-full-name" />
              </div>
              <div>
                <Label>Email *</Label>
                <Input type="email" value={createForm.email} onChange={e => setCreateForm({ ...createForm, email: e.target.value.toLowerCase() })} data-testid="create-email" />
              </div>
              <div>
                <Label>Password *</Label>
                <Input type="text" value={createForm.password} onChange={e => setCreateForm({ ...createForm, password: e.target.value })} placeholder="Set initial password" data-testid="create-password" />
                <p className="text-xs text-slate-500 mt-1">User can change later from their Profile</p>
              </div>
              <div>
                <Label>Role *</Label>
                <Select value={createForm.role} onValueChange={v => setCreateForm({ ...createForm, role: v })}>
                  <SelectTrigger data-testid="create-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="hr_manager">HR Manager</SelectItem>
                    <SelectItem value="hr_executive">HR Executive</SelectItem>
                    <SelectItem value="accounts_manager">Accounts Manager</SelectItem>
                    <SelectItem value="ops_manager">Ops Manager</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="employee">Employee</SelectItem>
                    <SelectItem value="warehouse_manager">Warehouse Manager</SelectItem>
                    <SelectItem value="state_head">State Head</SelectItem>
                    <SelectItem value="asm">ASM (Area Sales Manager)</SelectItem>
                    <SelectItem value="tl">Team Lead</SelectItem>
                    <SelectItem value="fse">Field Sales Executive (FSE)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={async () => {
                  if (!createForm.full_name || !createForm.email || !createForm.password) {
                    toast.error('All fields required'); return;
                  }
                  try {
                    setCreating(true);
                    await register(createForm);
                    toast.success(`User ${createForm.email} created with role ${createForm.role}`);
                    setCreateDialogOpen(false);
                    setCreateForm({ full_name: '', email: '', password: '', role: 'employee' });
                    fetchData();
                  } catch (e) {
                    toast.error(e.response?.data?.detail || 'Failed to create user');
                  } finally { setCreating(false); }
                }}
                disabled={creating || !createForm.full_name || !createForm.email || !createForm.password}
                data-testid="confirm-create-user"
              >
                {creating ? 'Creating...' : 'Create User'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Upload Users Dialog */}
        <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Bulk Upload Users</DialogTitle>
              <DialogDescription>
                Onboard many users at once via Excel. Best for large field teams (FSEs / TLs / ASMs).
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="rounded-lg border bg-blue-50 border-blue-200 p-3 text-sm">
                <p className="font-medium text-blue-900">Step 1 — Download Template</p>
                <p className="text-xs text-blue-700 mt-1">Get an Excel file with the correct columns + sample rows + valid roles reference.</p>
                <Button size="sm" variant="outline" className="mt-2" data-testid="download-bulk-template-btn"
                  onClick={async () => {
                    try {
                      const r = await downloadUsersBulkTemplate();
                      const blob = new Blob([r.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a'); a.href = url; a.download = 'users_bulk_template.xlsx'; a.click();
                      window.URL.revokeObjectURL(url);
                      toast.success('Template downloaded');
                    } catch { toast.error('Failed to download template'); }
                  }}>Download Template</Button>
              </div>

              <div className="rounded-lg border bg-slate-50 border-slate-200 p-3 text-sm">
                <p className="font-medium text-slate-900">Step 2 — Fill and Upload</p>
                <p className="text-xs text-slate-600 mt-1">Required columns: Full Name, Email, Password, Role. See 'Valid Roles' sheet for role values.</p>
                <Input type="file" accept=".xlsx,.xls" onChange={e => { setBulkFile(e.target.files[0]); setBulkResult(null); }} className="mt-2" data-testid="bulk-file-input" />
              </div>

              {bulkResult && (
                <div className="rounded-lg border p-3 text-sm">
                  <p className="font-semibold text-green-700">
                    ✓ Created {bulkResult.created} user{bulkResult.created === 1 ? '' : 's'}
                  </p>
                  {bulkResult.errors && bulkResult.errors.length > 0 && (
                    <div className="mt-2">
                      <p className="font-semibold text-red-700 mb-1">{bulkResult.errors.length} error{bulkResult.errors.length === 1 ? '' : 's'}:</p>
                      <div className="max-h-40 overflow-y-auto bg-red-50 p-2 rounded text-xs">
                        {bulkResult.errors.map((e, i) => (
                          <div key={i} className="text-red-800">Row {e.row}: {e.error}</div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBulkDialogOpen(false)}>Close</Button>
              <Button
                onClick={async () => {
                  if (!bulkFile) { toast.error('Please choose a file first'); return; }
                  try {
                    setBulkUploading(true);
                    const fd = new FormData(); fd.append('file', bulkFile);
                    const r = await bulkUploadUsers(fd);
                    setBulkResult(r.data);
                    if (r.data.created > 0) {
                      toast.success(`Created ${r.data.created} users`);
                      fetchData();
                    }
                    if (r.data.errors && r.data.errors.length > 0) {
                      toast.warning(`${r.data.errors.length} rows had errors`);
                    }
                  } catch (e) {
                    toast.error(e.response?.data?.detail || 'Bulk upload failed');
                  } finally { setBulkUploading(false); }
                }}
                disabled={!bulkFile || bulkUploading}
                data-testid="confirm-bulk-upload"
              >
                {bulkUploading ? 'Uploading...' : 'Upload & Create Users'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default UserManagement;
